/**
 * ConnectRunner — Wizard estilo Zapier MCP para conectar runners.
 * 7 steps: Nome → Token → Modo → Instalar → Verificar → Testar → Pronto
 */
import { T } from '../styles/tokens';
import { Spinner, Button } from '../components/ui';

import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '../api/client';
import { AccessScopeSelector } from '../components/AccessScopeSelector';

type Mode = 'vps' | 'openclaw';
type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;

const STEP_LABELS = [
  '', // 0
  'Criar',
  'Token',
  'Modo',     // skipped — mode selection is in step 1 now
  'Instalar',
  'Verificar',
  'Testar',
  'Pronto!',
];

const API_BASE = window.location.origin;

export function ConnectRunnerPage() {
  // Wizard state
  const [step, setStep] = useState<Step>(1);
  const [name, setName] = useState('');
  const [mode, setMode] = useState<Mode>('openclaw');
  const [runner, setRunner] = useState<{ id: string; token: string; name: string } | null>(null);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [checking, setChecking] = useState(false);
  const [runnerStatus, setRunnerStatus] = useState<'unknown' | 'online' | 'offline'>('unknown');

  // Existing runners
  const [runners, setRunners] = useState<any[]>([]);
  const [loadingRunners, setLoadingRunners] = useState(true);
  const [showExisting, setShowExisting] = useState(false);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  const [installMode, setInstallMode] = useState<'chat' | 'terminal'>('chat');
  const [autoChecking, setAutoChecking] = useState(false);
  const autoCheckRef = useRef<any>(null);

  // Access scopes for permissions
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['workspace', 'docs', 'memory', 'config', 'scripts', 'agents', 'tools', 'identity']);

  // Auto-check connection in background during step 4
  useEffect(() => {
    if (step !== 4 || !runner) return;
    setAutoChecking(true);
    let tries = 0;
    console.log('[AUTO-CHECK] Starting auto-check for runner:', runner.id);
    
    autoCheckRef.current = setInterval(async () => {
      tries++;
      try {
        const res = await api.get<any>(`/runners/${runner.id}`);
        const r = res?.runner;
        const lastSeen = r?.last_heartbeat_at || r?.last_seen_at;
        
        console.log(`[AUTO-CHECK] Attempt ${tries}: status=${r?.status}, lastSeen=${lastSeen}`);
        
        if (r?.status === 'online' && lastSeen) {
          const diff = Date.now() - new Date(lastSeen).getTime();
          console.log(`[AUTO-CHECK] Diff: ${diff}ms (threshold: 120000ms)`);
          
          if (diff < 120_000) {
 console.log('[AUTO-CHECK] SUCCESS! Jumping to step 6');
            clearInterval(autoCheckRef.current);
            setAutoChecking(false);
            setRunnerStatus('online');
            setStep(6);
            return;
          }
        }
      } catch (err) {
        console.error(`[AUTO-CHECK] Error on attempt ${tries}:`, err);
      }
      
      if (tries >= 60) { 
 console.log('[AUTO-CHECK] Max attempts reached, stopping');
        clearInterval(autoCheckRef.current); 
        setAutoChecking(false); 
      }
    }, 5000);

    return () => { if (autoCheckRef.current) clearInterval(autoCheckRef.current); };
  }, [step, runner]);
  const [regeneratedToken, setRegenToken] = useState<{ id: string; token: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [uninstallTarget, setUninstallTarget] = useState<string | null>(null); // runner id
  const [testingRunner, setTestingRunner] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { ok: boolean; msg: string }>>({});

  const pollRef = useRef<any>(null);

  useEffect(() => {
    api.get<any>('/runners').then((res: any) => {
      setRunners((res?.runners ?? []).filter((r: any) => r.status !== 'disabled'));
    }).catch(() => {}).finally(() => setLoadingRunners(false));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const copy = async (text: string, label: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(label); setTimeout(() => setCopied(''), 2000); } catch {}
  };

  // Step 1: Register
  const register = async () => {
    if (!name.trim()) return setError('Nome obrigatório');
    setRegistering(true); setError('');
    try {
 // VALIDATE SESSION FIRST
      const customerInfo = await api.get<any>('/runners/debug/whoami');
      // api.get() already unwraps .data, so customerInfo directly contains { customer_id, role, session_valid, debug }
      
      if (!customerInfo?.customer_id) {
 console.error('CRITICAL: customer_id is missing!', { customerInfo });
 throw new Error('ERRO CRÍTICO: Sua sessão está inválida. Faça logout e login novamente.');
      }
      
      if (customerInfo.customer_id === 'undefined' || customerInfo.customer_id.includes('undefined')) {
 console.error('CRITICAL: customer_id is "undefined" string!', { customerInfo });
 throw new Error('ERRO CRÍTICO: Sessão corrompida (customer_id = undefined). Faça logout e login novamente.');
      }
      
      if (!customerInfo.session_valid) {
 console.error('CRITICAL: Session validation failed!', { customerInfo });
 throw new Error('ERRO CRÍTICO: Sessão inválida. Faça logout e login novamente.');
      }
      
 console.log('Session valid!', { customer_id: customerInfo.customer_id });
      
 // NOW create runner
      const modeToUse = mode;
      const res = await api.post<any>('/runners/register', { name: name.trim(), runner_mode: modeToUse, capabilities: {} });
      setRunner({ id: res.runner_id, token: res.token, name: name.trim() });
      setStep(2);
    } catch (e: any) { setError(e?.message || 'Erro ao registrar'); }
    setRegistering(false);
  };

  // Step 5: Check connection (poll every 3s for up to 60s)
  const startChecking = () => {
    if (!runner) return;
    setChecking(true); setRunnerStatus('unknown');
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries++;
      try {
        const res = await api.get<any>(`/runners/${runner.id}`);
        const r = res?.runner;
        const lastSeen = r?.last_heartbeat_at || r?.last_seen_at;
        if (r?.status === 'online' && lastSeen) {
          const diff = Date.now() - new Date(lastSeen).getTime();
          if (diff < 120_000) {
            setRunnerStatus('online'); setChecking(false);
            clearInterval(pollRef.current);
            return;
          }
        }
      } catch {}
      if (tries >= 20) { setRunnerStatus('offline'); setChecking(false); clearInterval(pollRef.current); }
    }, 3000);
  };

  // Existing runner actions
  const regenerateToken = async (id: string) => {
    setRegenerating(id);
    try {
      const res = await api.post<any>(`/runners/${id}/regenerate`);
      setRegenToken({ id, token: res.token });
    } catch (e: any) { setError(e?.message || 'Erro'); }
    setRegenerating(null);
  };

  const testRunner = async (id: string) => {
    setTestingRunner(id);
    setTestResult((prev) => ({ ...prev, [id]: undefined as any }));
    try {
      let ok = 0;
      for (let i = 0; i < 3; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const res = await api.get<any>(`/runners/${id}`);
        const lastSeen = res?.runner?.last_heartbeat_at || res?.runner?.last_seen_at;
        const diff = Date.now() - new Date(lastSeen ?? 0).getTime();
        if (diff < 60_000) ok++;
      }
      if (ok >= 2) {
 setTestResult((prev) => ({ ...prev, [id]: { ok: true, msg: 'Runner ativo e respondendo!' } }));
      } else {
 setTestResult((prev) => ({ ...prev, [id]: { ok: false, msg: 'Heartbeat irregular. Processo pode não estar rodando.' } }));
      }
    } catch (e: any) {
 setTestResult((prev) => ({ ...prev, [id]: { ok: false, msg: ` ${e?.message || 'Erro'}` } }));
    }
    setTestingRunner(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/runners/${deleteTarget.id}`);
      setRunners(runners.filter(r => r.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e: any) { setError(e?.message || 'Erro'); }
    setDeleting(false);
  };

  const isOnline = (r: any) => {
    const ls = r.last_heartbeat_at || r.last_seen_at;
    return r.status === 'online' && ls && (Date.now() - new Date(ls).getTime()) < 120_000;
  };

  // Detailed status for UX
  const getRunnerHealth = (r: any): { icon: string; label: string; color: string; hint: string } => {
 if (r.status === 'disabled') return { icon: '', label: 'Desabilitado', color: T.textMuted, hint: 'Runner foi revogado. Crie um novo.' };
    const lastSeen = r.last_heartbeat_at || r.last_seen_at;
 if (!lastSeen) return { icon: '', label: 'Nunca conectou', color: T.danger, hint: 'O runner foi registrado mas nunca enviou heartbeat. Siga as instruções de instalação.' };
    const diffMs = Date.now() - new Date(lastSeen).getTime();
 if (diffMs < 120_000) return { icon: '', label: 'Online', color: T.success, hint: 'Runner conectado e pronto para executar.' };
 if (diffMs < 600_000) return { icon: '', label: 'Inativo', color: T.warning, hint: `Último sinal há ${Math.round(diffMs / 60000)} min. O processo pode ter parado.` };
 return { icon: '', label: 'Offline', color: T.danger, hint: `Sem sinal há ${Math.round(diffMs / 60000)} min. Verifique se o processo está rodando.` };
  };

  const runnerId = runner?.id ?? '<RUNNER_ID>';
  const runnerToken = runner?.token ?? '<RUNNER_TOKEN>';

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════

  return (
    <div>
      <div class="page-header">
        <h1><img src="/icons/openclaw.svg" alt="" width="28" height="28" style="vertical-align: -4px; margin-right: 8px" />Instalador v2</h1>
        <p style={{ fontSize: 13, color: T.textMuted, margin: '4px 0 0' }}>
          Crie, autentique e conecte seu executor em minutos.
        </p>
      </div>

      {/* Existing runners toggle */}
      {runners.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Button variant="secondary" size="sm" onClick={() => setShowExisting(!showExisting)}>
            {showExisting ? '▲ Fechar' : `▼ Meus Executores (${runners.length})`}
          </Button>
          {showExisting && (
            <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
              {runners.map((r: any) => {
                const health = getRunnerHealth(r);
                return (
                <div class="card" key={r.id} style={{ border: `1px solid ${health.color}`, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <div>
                      <span style={{ fontSize: 16 }}>{health.icon}</span>
                      <strong style={{ marginLeft: 8 }}>{r.name}</strong>
                      <span class="badge" style={{ marginLeft: 8, fontSize: 11, background: T.bgSecondary, padding: '2px 6px', borderRadius: 4 }}>
 {r.runner_mode === 'openclaw' ? 'OPENCLAW' : 'VPS'}
                      </span>
                      <span style={{ marginLeft: 8, fontSize: 12, color: health.color, fontWeight: 600 }}>
                        {health.label}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button variant="secondary" size="xs" onClick={() => testRunner(r.id)} disabled={testingRunner === r.id}>
 {testingRunner === r.id ? 'Testando...' : 'Testar'}
                      </Button>
                      <Button variant="secondary" size="xs" onClick={() => regenerateToken(r.id)} disabled={regenerating === r.id}>
                        {regenerating === r.id ? '...' : 'Novo Token'}
                      </Button>
                      <Button variant="secondary" size="xs" onClick={() => setUninstallTarget(uninstallTarget === r.id ? null : r.id)}>
 Desinstalar
                      </Button>
                      <Button variant="ghost" size="xs" style={{ color: T.danger }} onClick={() => setDeleteTarget(r)}>
                        Apagar
                      </Button>
                    </div>
                  </div>
                  {(r.last_heartbeat_at || r.last_seen_at) && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Último sinal: {new Date(r.last_heartbeat_at || r.last_seen_at).toLocaleString('pt-BR')}</div>}
                  {testResult[r.id] && (
                    <div style={{ marginTop: 6, padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: testResult[r.id].ok ? 'rgba(52,199,89,0.1)' : 'rgba(255,59,48,0.1)', color: testResult[r.id].ok ? T.success : T.danger }}>
                      {testResult[r.id].msg}
                    </div>
                  )}
                  {regeneratedToken?.id === r.id && (
                    <div style={{ marginTop: 8, padding: 8, background: 'rgba(255,180,0,0.1)', borderRadius: 6 }}>
 <div style={{ fontSize: 12, color: T.warning, fontWeight: 600 }}>Novo token — copie agora!</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        <code style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{regeneratedToken.token}</code>
                        <Button size="xs" onClick={() => copy(regeneratedToken.token, `regen-${r.id}`)}>
 {copied === `regen-${r.id}` ? '' : ''}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* ═══ UNINSTALL PANEL ═══ */}
                  {uninstallTarget === r.id && (
                    <UninstallPanel runner={r} onDisable={() => setDeleteTarget(r)} copy={copy} copied={copied} />
                  )}

                  {/* Status hint with actionable guidance */}
                  {uninstallTarget !== r.id && (
                    <div style={{ fontSize: 12, color: health.color === T.success ? T.textMuted : health.color, marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
 <span></span>
                      <span>{health.hint}</span>
                    </div>
                  )}

                  {/* Quick fix suggestions for non-online runners */}
                  {health.label !== 'Online' && health.label !== 'Desabilitado' && uninstallTarget !== r.id && (
                    <FixRunnerPanel runner={r} copy={copy} copied={copied} />
                  )}
                </div>
              ); })}
            </div>
          )}
        </div>
      )}

      {/* Stepper - visual progress bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 28, background: T.bgCard, borderRadius: 12, overflow: 'hidden', border: `1px solid ${T.border}`, overflowX: 'auto' }}>
        {([
          { s: 1, label: 'Criar' },
          { s: 2, label: 'Token' },
          { s: 4, label: 'Instalar' },
          { s: 6, label: 'Testar' },
        ]).map(({ s, label }, i, arr) => {
          const isActive = s === step;
          const isDone = s < step;
          return (
            <div key={s} style={{
              flex: 1, padding: '12px 4px', textAlign: 'center', position: 'relative', minWidth: 60,
              background: isActive ? T.primary : isDone ? T.successBg : 'transparent',
              borderRight: i < arr.length - 1 ? `1px solid ${T.border}` : 'none',
              transition: 'background 0.2s',
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', margin: '0 auto 6px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
                background: isActive ? '#fff' : isDone ? T.success : T.bgModal,
                color: isActive ? T.primary : isDone ? '#fff' : T.textSecondary,
                border: isActive ? 'none' : isDone ? 'none' : `1px solid ${T.textDisabled}`,
              }}>
                {isDone ? '' : i + 1}
              </div>
              <div class="stepper-label" style={{
                fontSize: 11, fontWeight: isActive ? 700 : 500,
                color: isActive ? '#fff' : isDone ? T.success : T.textSecondary,
              }}>
                {label}
              </div>
            </div>
          );
        })}
      </div>

 {error && <div class="error-banner" style={{ marginBottom: 16 }}> {error}</div>}

      {/* ═══ STEP 1: Create Runner ═══ */}
      {step === 1 && (
        <div class="card" style={{ padding: '24px 28px' }}>
          <h2 style={{ marginTop: 0, fontSize: 22 }}>Criar Executor</h2>
          <p style={{ fontSize: 14, color: T.textMuted, marginBottom: 20, lineHeight: 1.5 }}>
            O executor é o processo que roda suas ações de IA e automação. Dê um nome e escolha onde ele vai rodar.
          </p>

          {/* Nome */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 6 }}>
              Nome do Executor <span style={{ color: T.danger }}>*</span>
            </label>
            <p style={{ fontSize: 12, color: T.textMuted, margin: '0 0 8px' }}>
              Use algo descritivo para identificar depois (ex: "Meu MacBook", "VPS Hetzner", "Servidor Principal")
            </p>
            <Input
              value={name}
              onChange={setName}
              placeholder="Ex: Servidor Principal"
              style={{ maxWidth: 480, fontSize: 14, padding: '10px 14px' }}
            />
          </div>

          {/* Permissões (Access Scopes) */}
          <div style={{ marginBottom: 24, padding: 16, background: 'rgba(102, 126, 234, 0.08)', borderRadius: 10, border: '1px solid rgba(102, 126, 234, 0.2)' }}>
            <label style={{ fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 12 }}>
 Permissões do Executor
            </label>
            <p style={{ fontSize: 13, color: T.textMuted, margin: '0 0 12px' }}>
              Escolha quais áreas o executor pode acessar. Você pode mudar isso depois.
            </p>
            <AccessScopeSelector selectedScopes={selectedScopes} onChange={setSelectedScopes} />
          </div>

          {/* Modo de execução — 2 cards grandes */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 12 }}>
              Modo de Execução
            </label>
            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: '1fr' }} class="mode-cards">
              {/* OpenClaw card */}
              <div
                onClick={() => setMode('openclaw')}
                style={{
                  padding: '24px 20px', borderRadius: 12, cursor: 'pointer',
                  border: mode === 'openclaw' ? `2px solid ${T.accent}` : `1px solid ${T.border}`,
                  background: mode === 'openclaw' ? T.warningBg : T.bgSecondary,
                  transition: 'all 0.2s',
                  position: 'relative',
                }}
              >
                {mode === 'openclaw' && (
                  <div style={{ position: 'absolute', top: 12, right: 12, width: 22, height: 22, borderRadius: '50%', background: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}></div>
                )}
                <div style={{ marginBottom: 12 }}><img src="/icons/openclaw.svg" alt="Orquestrador" width="40" height="40" /></div>
                <strong style={{ fontSize: 16, display: 'block', marginBottom: 6 }}>Orquestrador</strong>
                <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: T.successBg, color: T.success, marginBottom: 10 }}>
 Recomendado
                </span>
                <p style={{ fontSize: 13, color: T.textMuted, margin: 0, lineHeight: 1.5 }}>
                  Roda dentro do orquestrador, sem precisar de servidor extra. Setup rápido em 2 minutos.
                </p>
                <ul style={{ fontSize: 12, color: T.textMuted, margin: '10px 0 0', paddingLeft: 16, lineHeight: 1.8 }}>
                  <li>Sem infraestrutura adicional</li>
                  <li>Configuração via JSON</li>
                  <li>Atualização automática</li>
                </ul>
              </div>

              {/* VPS card */}
              <div
                onClick={() => setMode('vps')}
                style={{
                  padding: '24px 20px', borderRadius: 12, cursor: 'pointer',
                  border: mode === 'vps' ? '2px solid var(--accent)' : `1px solid ${T.border}`,
                  background: mode === 'vps' ? T.warningBg : T.bgSecondary,
                  transition: 'all 0.2s',
                  position: 'relative',
                }}
              >
                {mode === 'vps' && (
                  <div style={{ position: 'absolute', top: 12, right: 12, width: 22, height: 22, borderRadius: '50%', background: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}></div>
                )}
 <div style={{ fontSize: 40, marginBottom: 12 }}></div>
                <strong style={{ fontSize: 16, display: 'block', marginBottom: 6 }}>VPS / Servidor</strong>
                <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: T.border, color: T.textMuted, marginBottom: 10 }}>
                  Avançado
                </span>
                <p style={{ fontSize: 13, color: T.textMuted, margin: 0, lineHeight: 1.5 }}>
                  Roda em qualquer servidor com Node.js. Mais controle sobre recursos e rede.
                </p>
                <ul style={{ fontSize: 12, color: T.textMuted, margin: '10px 0 0', paddingLeft: 16, lineHeight: 1.8 }}>
                  <li>Controle total do ambiente</li>
                  <li>PM2 / Docker / systemd</li>
                  <li>Ideal para alto volume</li>
                </ul>
              </div>
            </div>
          </div>

          <Button
            size="lg"
            onClick={register}
            disabled={registering || !name.trim()}
            loading={registering}
          >
            {registering ? 'Registrando...' : 'Criar Executor'}
          </Button>
        </div>
      )}

      {/* ═══ STEP 2: Token Generated ═══ */}
      {step === 2 && runner && (
        <div class="card">
          <h2 style={{ marginTop: 0, marginBottom: 8 }}>Token Gerado</h2>
          <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 20, lineHeight: 1.5 }}>
            Salve suas credenciais em um local seguro. O token <strong style={{ color: T.warning }}>não pode ser recuperado</strong> depois.
          </p>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: T.textMuted, marginBottom: 4, display: 'block' }}>Runner ID</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code style={{ flex: 1, padding: '10px 14px', background: T.bgSecondary, borderRadius: 8, fontSize: 12, color: T.textMuted, border: `1px solid ${T.border}` }}>{runner.id}</code>
              <Button variant="secondary" size="sm" style={{ opacity: 0.7 }} onClick={() => copy(runner.id, 'id')}>
 {copied === 'id' ? '' : 'Copiar'}
              </Button>
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, color: T.textMuted, marginBottom: 4, display: 'block' }}>Runner Token</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code style={{ flex: 1, padding: '10px 14px', background: T.bgSecondary, borderRadius: 8, fontSize: 11, wordBreak: 'break-all', color: T.textMuted, border: `1px solid ${T.border}`, letterSpacing: '0.5px' }}>{runner.token}</code>
              <Button variant="secondary" size="sm" style={{ opacity: 0.7 }} onClick={() => copy(runner.token, 'token')}>
 {copied === 'token' ? '' : 'Copiar'}
              </Button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <Button variant="secondary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flex: 1 }} onClick={() => {
              const content = `# WhiteLabelClaw Runner Credentials\n# Guarde este arquivo em local seguro!\n\nRUNNER_ID=${runner.id}\nRUNNER_TOKEN=${runner.token}\nRUNNER_API_URL=${window.location.origin}\n`;
              const blob = new Blob([content], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = 'runner-credentials.env'; a.click();
              URL.revokeObjectURL(url);
            }}>
 Baixar credenciais (.env)
            </Button>
          </div>

          <div style={{ textAlign: 'left', marginTop: 24 }}><Button size="lg" onClick={() => setStep(4)}>Seguir para instalação →</Button></div>
        </div>
      )}

      {/* ═══ STEP 3: Choose Mode ═══ */}
      {step === 3 && (
        <div class="card">
          <h2 style={{ marginTop: 0 }}>Escolher Modo de Instalação</h2>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr', marginBottom: 16 }}>
            <div onClick={() => setMode('openclaw')} style={{
              padding: 20, borderRadius: 8, cursor: 'pointer', textAlign: 'center',
              border: mode === 'openclaw' ? `2px solid ${T.accent}` : `1px solid ${T.border}`,
              background: mode === 'openclaw' ? T.warningBg : T.bgSecondary,
            }}>
              <div><img src="/icons/openclaw.svg" alt="Orquestrador" width="36" height="36" /></div>
              <strong style={{ fontSize: 15 }}>Orquestrador</strong>
              <p style={{ fontSize: 12, color: T.textMuted, margin: '8px 0 0' }}>
                Runner roda dentro do orquestrador.<br/>Sem servidor extra. Recomendado.
              </p>
 <span class="badge badge-success" style={{ marginTop: 8, fontSize: 11 }}>Recomendado</span>
            </div>
            <div onClick={() => setMode('vps')} style={{
              padding: 20, borderRadius: 8, cursor: 'pointer', textAlign: 'center',
              border: mode === 'vps' ? '2px solid var(--accent)' : `1px solid ${T.border}`,
              background: mode === 'vps' ? T.warningBg : T.bgSecondary,
            }}>
 <div style={{ fontSize: 36 }}></div>
              <strong style={{ fontSize: 15 }}>VPS (Node.js)</strong>
              <p style={{ fontSize: 12, color: T.textMuted, margin: '8px 0 0' }}>
                Runner em servidor externo.<br/>Mais controle, precisa de VPS.
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'left', marginTop: 24 }}><Button size="lg" onClick={() => setStep(4)}>Seguir para instalação →</Button></div>
        </div>
      )}

      {/* ═══ STEP 4: Install ═══ */}
      {step === 4 && (
        <div class="card">
          <h2 style={{ marginTop: 0, marginBottom: 20 }}>Instalar plugin WhiteLabelClaw</h2>

          {/* Mode selection cards */}
          <p style={{ fontSize: 14, color: T.textMuted, marginBottom: 12 }}>Como você deseja instalar:</p>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr', marginBottom: 24 }}>
            <div onClick={() => setInstallMode('chat')} style={{
              padding: 20, borderRadius: 10, cursor: 'pointer', textAlign: 'center',
              border: installMode === 'chat' ? `2px solid ${T.primary}` : `1px solid ${T.border}`,
              background: installMode === 'chat' ? T.primaryBg : T.bgSecondary,
              transition: 'all 0.2s',
            }}>
              {installMode === 'chat' && (
                <div style={{ fontSize: 11, color: T.primary, fontWeight: 700, marginBottom: 8 }}>Selecionado</div>
              )}
              <div style={{ marginBottom: 8 }}><img src="/icons/openclaw.svg" alt="Orquestrador" width="36" height="36" /></div>
              <strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }}>Chat do Agente</strong>
              <p style={{ fontSize: 12, color: T.textMuted, margin: 0, lineHeight: 1.4 }}>
                Envie um comando no chat e o agente instala automaticamente
              </p>
 <span class="badge badge-success" style={{ marginTop: 10, fontSize: 11, display: 'inline-block' }}>Recomendado</span>
            </div>

            <div onClick={() => setInstallMode('terminal')} style={{
              padding: 20, borderRadius: 10, cursor: 'pointer', textAlign: 'center',
              border: installMode === 'terminal' ? `2px solid ${T.primary}` : `1px solid ${T.border}`,
              background: installMode === 'terminal' ? T.primaryBg : T.bgSecondary,
              transition: 'all 0.2s',
            }}>
              {installMode === 'terminal' && (
                <div style={{ fontSize: 11, color: T.primary, fontWeight: 700, marginBottom: 8 }}>Selecionado</div>
              )}
 <div style={{ fontSize: 32, marginBottom: 8 }}></div>
              <strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }}>Via Terminal</strong>
              <p style={{ fontSize: 12, color: T.textMuted, margin: 0, lineHeight: 1.4 }}>
                Copie os comandos e execute no terminal do seu servidor
              </p>
              <span class="badge" style={{ marginTop: 10, fontSize: 11, display: 'inline-block', background: T.border, color: T.textMuted }}>Manual</span>
            </div>
          </div>

          {/* ═══ Chat mode ═══ */}
          {installMode === 'chat' && (
            <div style={{ padding: 24, background: T.primaryBg, border: '1px solid rgba(37,99,235,0.15)', borderRadius: 10 }}>
              <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: T.primary, color: T.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>1</div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '2px 0 8px', fontSize: 15 }}>Copie e cole no chat do agente</h4>
                  <div style={{ background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 8, padding: '14px 16px', fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all', color: T.text, lineHeight: 1.6, marginBottom: 12 }}>
                    install /docs/openclaw-runner-setup.md?runner_id={runner?.id}&runner_token={runner?.token}&api_url=${window.location.origin}
                  </div>
                  <Button size="sm" onClick={() => {
                    const url = `install /docs/openclaw-runner-setup.md?runner_id=${runner?.id || ''}&runner_token=${runner?.token || ''}&api_url=${window.location.origin}`;
                    copy(url, 'doc');
                  }}>
                    {copied === 'doc' ? 'Copiado!' : 'Copiar comando'}
                  </Button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: T.bgSecondary, color: T.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0, border: `1px solid ${T.border}` }}>2</div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '2px 0 6px', fontSize: 15 }}>Aguarde a instalação</h4>
                  <p style={{ fontSize: 13, color: T.textSecondary, margin: 0, lineHeight: 1.6 }}>
                    A instalação leva de <strong>2 a 10 minutos</strong>. Esta página avança automaticamente assim que o processo for concluído. Apenas aguarde.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ═══ Terminal mode ═══ */}
          {installMode === 'terminal' && (
            <div>
              <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>
                <strong>Passo 1:</strong> Crie o arquivo <code>.env</code> no diretório do runner:
              </p>

              <CodeBlock label="Arquivo .env" code={`RUNNER_API_URL=${API_BASE}\nRUNNER_ID=${runnerId}\nRUNNER_TOKEN=${runnerToken}\nRUNNER_POLL_INTERVAL_MS=30000\nRUNNER_PULL_LIMIT=5`} onCopy={() => copy(`RUNNER_API_URL=${API_BASE}\nRUNNER_ID=${runnerId}\nRUNNER_TOKEN=${runnerToken}\nRUNNER_POLL_INTERVAL_MS=30000\nRUNNER_PULL_LIMIT=5`, 'env-t')} copied={copied === 'env-t'} />

              <p style={{ fontSize: 13, color: T.textMuted, margin: '16px 0 8px' }}>
                <strong>Passo 2:</strong> Inicie o runner:
              </p>

              <CodeBlock label="Iniciar runner" code={`# Modo loop (recomendado — roda continuamente):\nnode index.js --loop\n\n# Ou com PM2 (mantém rodando após reinício):\nnpm install -g pm2\npm2 start index.js --name app-runner -- --loop\npm2 save && pm2 startup`} onCopy={() => copy(`node index.js --loop`, 'start-t')} copied={copied === 'start-t'} />

              <p style={{ fontSize: 13, color: T.textMuted, margin: '16px 0 8px' }}>
                <strong>Opcional:</strong> Configuração JSON (alternativa ao .env):
              </p>

              <CodeBlock label="JSON Config" code={JSON.stringify({ api_base: API_BASE, runner_id: runnerId, runner_token: runnerToken, poll_interval_ms: 30000 }, null, 2)} onCopy={() => copy(JSON.stringify({ api_base: API_BASE, runner_id: runnerId, runner_token: runnerToken, poll_interval_ms: 30000 }), 'json')} copied={copied === 'json'} />
            </div>
          )}

          {/* Auto-check status + manual link */}
          {installMode && (
            <div style={{ marginTop: 20, textAlign: 'center' }}>
              {autoChecking && (
                <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Spinner size="sm" inline />
                  Aguardando conexão do runner...
                </p>
              )}
              <Button variant="ghost" size="sm" style={{ textDecoration: 'underline' }} onClick={() => { if (autoCheckRef.current) clearInterval(autoCheckRef.current); setStep(6); startChecking(); }}>
                Verificar conexão manualmente →
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ═══ STEP 5: Verify Connection ═══ */}
      {step === 5 && (
        <div class="card" style={{ textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>Verificar Conexão</h2>

          {checking && (
            <div style={{ margin: '32px 0' }}>
 <div style={{ fontSize: 48, marginBottom: 16 }}></div>
              <p style={{ fontSize: 15, fontWeight: 600 }}>Aguardando heartbeat do runner...</p>
              <p style={{ fontSize: 13, color: T.textMuted }}>
                Verificando a cada 3 segundos (até 60s). Certifique-se de que o runner está rodando.
              </p>
              <div style={{ margin: '16px auto', textAlign: 'center' }}><Spinner size="lg" /></div>
            </div>
          )}

          {runnerStatus === 'online' && (
            <div style={{ margin: '32px 0' }}>
 <div style={{ fontSize: 64, marginBottom: 16 }}></div>
              <h3 style={{ color: T.success, fontSize: 20 }}>Conectado!</h3>
              <p style={{ fontSize: 13, color: T.textMuted }}>
                Runner <strong>"{runner?.name}"</strong> está online e enviando heartbeats.
              </p>
              <Button style={{ marginTop: 16 }} onClick={() => setStep(6)}>
                Próximo → Testar
              </Button>
            </div>
          )}

          {runnerStatus === 'offline' && (
            <div style={{ margin: '32px 0' }}>
 <div style={{ fontSize: 64, marginBottom: 16 }}></div>
              <h3 style={{ color: T.danger, fontSize: 20 }}>Runner não detectado</h3>
              <p style={{ fontSize: 13, color: T.textMuted }}>
                Nenhum heartbeat recebido em 60 segundos. Verifique:
              </p>
              <ul style={{ textAlign: 'left', fontSize: 13, maxWidth: 400, margin: '12px auto', lineHeight: 1.8 }}>
                <li>O processo do runner está rodando?</li>
                <li>O <code>RUNNER_TOKEN</code> está correto?</li>
                <li>O servidor consegue acessar <code>{API_BASE}</code>?</li>
                <li>Firewall não bloqueia saída HTTPS (443)?</li>
              </ul>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
                <Button variant="secondary" onClick={() => { startChecking(); }}>Tentar novamente</Button>
                <Button onClick={() => setStep(4)}>← Voltar para Instalar</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ STEP 6: Test Run ═══ */}
      {step === 6 && (
        <>
          {runnerStatus === 'online' && (
            <div style={{ padding: '14px 18px', background: T.successBg, border: `1px solid ${T.successBgStrong}`, borderRadius: 10, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
 <span style={{ fontSize: 20 }}></span>
              <div>
                <strong style={{ fontSize: 14 }}>Executor conectado com sucesso!</strong>
                <p style={{ fontSize: 12, color: T.textMuted, margin: '2px 0 0' }}>
                  Runner "{runner?.name}" está online. Agora vamos testar a execução.
                </p>
              </div>
            </div>
          )}
          <TestExecutionStep runnerId={runner?.id ?? ''} onNext={() => setStep(7)} />
        </>
      )}

      {/* ═══ STEP 7: Done! ═══ */}
      {/* Step 7 removed — completion is now inline in TestExecutionStep */}

      {/* ═══ Troubleshooting ═══ */}
      <div class="card" style={{ marginTop: 24 }}>
 <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Dúvidas Frequentes</h3>
        <Faq q="Runner aparece como 'offline'" a="Verifique se o processo está rodando (pm2 status ou ps aux | grep runner). O runner envia heartbeat a cada 30s — se last_seen > 2min, é marcado offline." />
        <Faq q="Erro 401 — Token inválido" a="Token pode ter sido regenerado. Use 'Novo Token' na lista de runners e atualize o .env ou config." />
        <Faq q="Erro 429 — Rate limit" a="Limite: 120 req/min. Aumente RUNNER_POLL_INTERVAL_MS (ex: 60000) se persistir." />
        <Faq q="Job travado (lease expirou)" a="Jobs com lease > 2min expiram e voltam para 'pending'. Após esgotar max_attempts, o job é marcado 'failed'." />
        <Faq q="Como desinstalar o runner?" a="Na lista de runners, clique para desabilitar. No servidor: pm2 delete app-runner ou pare o processo. No orquestrador: openclaw plugin remove elon-runner." />
      </div>

      {/* Delete modal */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => !deleting && setDeleteTarget(null)}>
          <div class="card" style={{ maxWidth: 400, margin: 20 }} onClick={(e: any) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px' }}>Excluir Runner</h3>
            <p>Excluir <strong>"{deleteTarget.name}"</strong>? O runner será desabilitado permanentemente.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancelar</Button>
              <Button variant="danger" onClick={confirmDelete} disabled={deleting} loading={deleting}>
                {deleting ? 'Excluindo...' : 'Excluir'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for spinner */}
      {/* spin removido — usa <Spinner /> */}
    </div>
  );
}

// ═══ Test Execution Component ═══

function TestExecutionStep({ runnerId, onNext }: { runnerId: string; onNext: () => void }) {
  const [phase, setPhase] = useState<'ready' | 'running' | 'success' | 'failed'>('ready');
  const [msg, setMsg] = useState('');
  const [durationMs, setDurationMs] = useState(0);

  const runTest = async () => {
    setPhase('running');
    setMsg('Enviando job de teste para o runner...');
    const startTime = Date.now();

    try {
      // Poll runner status up to 5 times (every 8s = 40s total)
      // Runner sends heartbeat every ~30s, so we give enough window
      const maxAttempts = 5;
      const intervalMs = 8000;
      let lastSeenOk = false;
      let lastDiff = Infinity;

      for (let i = 0; i < maxAttempts; i++) {
        if (i > 0) await new Promise(r => setTimeout(r, intervalMs));
        setMsg(`Verificando runner... (${i + 1}/${maxAttempts})`);

        const check = await api.get<any>(`/runners/${runnerId}`);
        const r = check?.runner;

        if (!r) continue;

        // Runner is online and last_seen within 2 minutes = healthy
        lastDiff = Date.now() - new Date(r.last_heartbeat_at || r.last_seen_at || 0).getTime();
        if (r.status === 'online' && lastDiff < 120_000) {
          lastSeenOk = true;
          break;
        }
      }

      const elapsed = Date.now() - startTime;
      setDurationMs(elapsed);

      if (lastSeenOk) {
        setPhase('success');
        setMsg(`Runner está ativo e respondendo! (${Math.round(elapsed / 1000)}s)`);
      } else {
        setPhase('failed');
        setMsg(lastDiff < 300_000
          ? 'Runner registrado mas heartbeat irregular. Verifique se o processo está rodando continuamente.'
          : 'Runner não está online. Verifique se o processo está rodando.'
        );
      }
    } catch (e: any) {
      setPhase('failed');
      setMsg(e?.message || 'Erro ao verificar runner');
    }
  };

  return (
    <div class="card" style={{ textAlign: 'center' }}>
      {phase !== 'success' && <h2 style={{ marginTop: 0 }}>Testar Execução</h2>}

      {phase === 'ready' && (
        <>
 <div style={{ fontSize: 48, marginBottom: 16 }}></div>
          <p style={{ fontSize: 14 }}>
            Vamos verificar se o runner está realmente executando e respondendo.
          </p>
          <p style={{ fontSize: 13, color: T.textMuted, marginTop: 8 }}>
            O teste verifica se o runner mantém heartbeats ativos (sinal de que está rodando o loop de execução).
          </p>
          <Button style={{ marginTop: 20 }} onClick={runTest}>
            Iniciar Teste
          </Button>
          <div style={{ marginTop: 12 }}>
            <Button variant="secondary" size="sm" onClick={onNext}>
              Pular teste →
            </Button>
          </div>
        </>
      )}

      {phase === 'running' && (
        <>
          <div style={{ margin: '16px auto', textAlign: 'center' }}><Spinner size="lg" /></div>
          <p style={{ fontSize: 14, fontWeight: 600, marginTop: 16 }}>{msg}</p>
          <p style={{ fontSize: 12, color: T.textMuted }}>Isso leva ~10 segundos...</p>
        </>
      )}

      {phase === 'success' && (
        <>
 <div style={{ fontSize: 64, marginBottom: 16 }}></div>
          <h3 style={{ color: T.success, fontSize: 22 }}>Pronto para começar!</h3>
          <p style={{ fontSize: 13, color: T.textMuted, marginTop: 8 }}>{msg}</p>
          <div style={{ margin: '20px auto', maxWidth: 320, padding: 12, background: T.bgSecondary, borderRadius: 8, textAlign: 'left', fontSize: 13 }}>
 <div>Heartbeat ativo</div>
 <div>Processo rodando</div>
 <div>Pronto para executar ações</div>
          </div>
          <p style={{ fontSize: 13, color: T.textMuted, marginTop: 16 }}>
            Configure suas integrações (API keys) para começar a executar ações com IA.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 20 }}>
            <Button onClick={() => { window.location.href = '/integrations'; }}>
              Configurar Integrações
            </Button>
            <Button variant="secondary" onClick={() => { window.location.href = '/'; }}>
              Ir para o Dashboard
            </Button>
          </div>
        </>
      )}

      {phase === 'failed' && (
        <>
 <div style={{ fontSize: 64, marginBottom: 16 }}></div>
          <h3 style={{ color: T.danger, fontSize: 20 }}>Teste falhou</h3>
          <p style={{ fontSize: 13, color: T.textMuted, marginTop: 8 }}>{msg}</p>
          <div style={{ margin: '20px auto', maxWidth: 400, padding: 16, background: T.bgSecondary, borderRadius: 8, textAlign: 'left', fontSize: 13 }}>
 <strong>Verifique:</strong>
            <ol style={{ margin: '8px 0 0', paddingLeft: 20, lineHeight: 1.8 }}>
              <li>O processo do runner está rodando? (<code>ps aux | grep runner</code>)</li>
              <li>O token no <code>.env</code> está correto?</li>
              <li>O runner consegue acessar <code>/</code>?</li>
              <li>Veja os logs do runner para mais detalhes</li>
            </ol>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
            <Button onClick={() => { setPhase('ready'); setMsg(''); }}>Tentar novamente</Button>
            <Button variant="secondary" onClick={onNext}>Pular →</Button>
          </div>
        </>
      )}
    </div>
  );
}

// ═══ Fix Runner Panel ═══

function FixRunnerPanel({ runner, copy, copied }: { runner: any; copy: (t: string, l: string) => void; copied: string }) {
  const [tab, setTab] = useState<'chat' | 'terminal'>('chat');

  return (
    <div style={{ marginTop: 8, padding: 12, background: T.bgSecondary, borderRadius: 6, fontSize: 12 }}>
      <strong style={{ fontSize: 13 }}>Como resolver:</strong>
      <div style={{ display: 'flex', gap: 4, margin: '8px 0' }}>
        <Button size="xs" variant={tab === 'chat' ? 'primary' : 'secondary'} onClick={() => setTab('chat')}>Chat do Agente</Button>
        <Button size="xs" variant={tab === 'terminal' ? 'primary' : 'secondary'} onClick={() => setTab('terminal')}>Terminal</Button>
      </div>

      {tab === 'chat' && (
        <div style={{ lineHeight: 1.8 }}>
          <p style={{ color: T.textSecondary, margin: '0 0 8px' }}>
            Envie este comando no chat do seu agente para reiniciar o runner automaticamente:
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            <div style={{ flex: 1, background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 6, padding: '8px 12px', fontFamily: 'monospace', fontSize: 11, color: T.text, wordBreak: 'break-all' }}>
              O runner do WhiteLabelClaw parou de funcionar. Verifique o processo, reinicie se necessário e confirme que está online.
            </div>
            <Button size="xs" style={{ flexShrink: 0 }} onClick={() => copy('O runner do WhiteLabelClaw parou de funcionar. Verifique o processo, reinicie se necessário e confirme que está online.', 'fix-chat')}>
 {copied === 'fix-chat' ? '' : 'Copiar'}
            </Button>
          </div>
        </div>
      )}

      {tab === 'terminal' && (
        <ol style={{ margin: '4px 0 0', paddingLeft: 20, lineHeight: 1.8 }}>
          {!(runner.last_heartbeat_at || runner.last_seen_at) ? (
            <>
              <li>Copie o token do runner (clique "Novo Token" se perdeu)</li>
              <li>Configure o <code>.env</code> com <code>RUNNER_ID</code> e <code>RUNNER_TOKEN</code></li>
              <li>Rode: <code>node runner/index.js --loop</code></li>
              <li>O status mudará para Online em ~30 segundos</li>
            </>
          ) : (
            <>
              <li>Verifique se está rodando: <code>ps aux | grep runner</code></li>
              <li>Reinicie: <code>cd runner && node index.js --loop</code></li>
              <li>Se o token foi regenerado, atualize o <code>.env</code></li>
            </>
          )}
        </ol>
      )}
    </div>
  );
}

// ═══ Helper Components ═══

function CodeBlock({ label, code, onCopy, copied }: { label: string; code: string; onCopy: () => void; copied: boolean }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ position: 'relative', background: T.bgSecondary, borderRadius: 8, padding: '12px 48px 12px 12px', overflow: 'auto' }}>
        <pre style={{ margin: 0, fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{code}</pre>
        <Button size="xs" onClick={onCopy} style={{ position: 'absolute', top: 8, right: 8 }}>
 {copied ? '' : ''}
        </Button>
      </div>
    </div>
  );
}

function UninstallPanel({ runner, onDisable, copy, copied }: { runner: any; onDisable: () => void; copy: (t: string, l: string) => void; copied: string }) {
  const [tab, setTab] = useState<'chat' | 'vps' | 'openclaw'>('chat');
  const rName = runner.name || 'app-runner';

  return (
    <div style={{ marginTop: 12, padding: 16, background: 'rgba(255,59,48,0.05)', border: '1px solid rgba(255,59,48,0.2)', borderRadius: 8 }}>
 <h4 style={{ margin: '0 0 8px', fontSize: 14, color: T.danger }}>Desinstalar Runner "{runner.name}"</h4>
      <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>
        Siga os passos abaixo para remover completamente o runner. Depois, clique "Apagar" para desabilitar o token no servidor.
      </p>

      {/* Link para Manual Completo */}
      <div style={{ marginBottom: 12, padding: 12, background: 'rgba(102, 126, 234, 0.1)', borderRadius: 6, border: '1px solid rgba(102, 126, 234, 0.3)' }}>
        <a href="//manuais/desinstalar" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#667eea', textDecoration: 'none', fontWeight: 600, fontSize: 12 }}>
 <span></span>
          <span>Ver Manual Completo Interativo</span>
          <span>→</span>
        </a>
        <p style={{ fontSize: 11, color: T.textMuted, margin: '6px 0 0', lineHeight: 1.4 }}>
          Guia passo-a-passo com 8 fases, botões para copiar comandos e verificação final.
        </p>
      </div>

      {/* Tab selector */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        <Button size="xs" variant={tab === 'chat' ? 'primary' : 'secondary'} onClick={() => setTab('chat')}>Chat do Agente</Button>
        <Button size="xs" variant={tab === 'vps' ? 'primary' : 'secondary'} onClick={() => setTab('vps')}>VPS</Button>
        <Button size="xs" variant={tab === 'openclaw' ? 'primary' : 'secondary'} onClick={() => setTab('openclaw')}>Terminal</Button>
      </div>

      {tab === 'chat' && (
        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
          <p style={{ color: T.textMuted, marginBottom: 12 }}>
            A maneira mais fácil: envie um destes comandos no chat do seu agente e ele removerá tudo automaticamente.
          </p>
          <strong>Português:</strong>
          <UninstallCode code={`Desinstale o runner do WhiteLabelClaw completamente do servidor.\n//manuais/desinstalar.md`} copy={copy} copied={copied} label="chat-pt" />
          <strong>English:</strong>
          <UninstallCode code={`Uninstall the WhiteLabelClaw runner completely from the server.\n//manuais/desinstalar.md`} copy={copy} copied={copied} label="chat-en" />
          <p style={{ color: T.textMuted, fontSize: 11 }}>
            O agente vai parar o processo, apagar todos os arquivos do runner e confirmar a remoção. Depois, clique em "Apagar Token" abaixo para desabilitar no servidor.
          </p>
        </div>
      )}

      {tab === 'vps' && (
        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
          <strong>1. Parar o processo:</strong>
          <UninstallCode code={`# Se usa PM2:\npm2 stop ${rName} && pm2 delete ${rName} && pm2 save\n\n# Se usa systemd:\nsudo systemctl stop ${rName}\nsudo systemctl disable ${rName}\nsudo rm /etc/systemd/system/${rName}.service\nsudo systemctl daemon-reload\n\n# Se roda manual (nohup/screen):\nkill $(pgrep -f "node.*elon-executor\\|node.*index.js.*runner")`} copy={copy} copied={copied} label="stop" />

          <strong>2. Remover arquivos:</strong>
          <UninstallCode code={`# Remover diretório do runner\nrm -rf ~/runner/\n# Ou se instalou em outro lugar:\nrm -rf /opt/app-runner/`} copy={copy} copied={copied} label="rm-files" />

          <strong>3. Remover variáveis de ambiente:</strong>
          <UninstallCode code={`# Remover .env do runner\nrm -f ~/runner/.env\n\n# Se adicionou ao perfil do sistema:\n# Editar ~/.bashrc ou /etc/environment e remover:\n# RUNNER_API_URL, RUNNER_ID, RUNNER_TOKEN`} copy={copy} copied={copied} label="rm-env" />

          <strong>4. Docker (se aplicável):</strong>
          <UninstallCode code={`docker stop ${rName} && docker rm ${rName}\n# Se usou imagem local:\ndocker rmi app/runner:latest`} copy={copy} copied={copied} label="docker-rm" />

          <strong>5. Confirmar:</strong>
          <p style={{ color: T.textMuted }}>
            Verifique que nenhum processo está rodando: <code>ps aux | grep runner</code>
          </p>
        </div>
      )}

      {tab === 'openclaw' && (
        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
          <strong>1. Parar o processo do runner:</strong>
          <UninstallCode code={`# Encontrar e parar o processo:\nkill $(pgrep -f "openclaw-runner")\n\n# Ou se usa PM2:\npm2 stop openclaw-runner && pm2 delete openclaw-runner && pm2 save`} copy={copy} copied={copied} label="oc-stop" />

          <strong>2. Remover arquivos de configuração:</strong>
          <UninstallCode code={`# Remover config e .env do runner\nrm -f runner/openclaw-runner.json\nrm -f runner/elon-executor.json\nrm -f runner/.env`} copy={copy} copied={copied} label="oc-config" />

          <strong>3. Limpar logs e dados locais:</strong>
          <UninstallCode code={`rm -f runner/executor.log\nrm -f runner/runner.log\nrm -rf runner/.artifacts/`} copy={copy} copied={copied} label="oc-logs" />

          <strong>4. Confirmar:</strong>
          <p style={{ color: T.textMuted }}>
            Verifique que não há processo rodando: <code>ps aux | grep openclaw-runner</code>
          </p>
        </div>
      )}

      <div style={{ marginTop: 16, padding: 12, background: 'rgba(255,59,48,0.08)', borderRadius: 6 }}>
 <strong style={{ fontSize: 12, color: T.danger }}>Passo final: Apagar token no servidor</strong>
        <p style={{ fontSize: 12, color: T.textMuted, margin: '4px 0 8px' }}>
          Isso desabilita o token permanentemente. O runner não poderá mais se conectar.
        </p>
        <Button variant="danger" size="sm" onClick={onDisable}>
          Apagar Token e Desabilitar Runner
        </Button>
      </div>
    </div>
  );
}

function UninstallCode({ code, copy, copied, label }: { code: string; copy: (t: string, l: string) => void; copied: string; label: string }) {
  return (
    <div style={{ position: 'relative', background: T.bgSecondary, borderRadius: 6, padding: '8px 40px 8px 10px', margin: '4px 0 12px', overflow: 'auto' }}>
      <pre style={{ margin: 0, fontSize: 11, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{code}</pre>
      <Button variant="ghost" size="xs" onClick={() => copy(code, `uninstall-${label}`)} style={{ position: 'absolute', top: 4, right: 4 }}>
 {copied === `uninstall-${label}` ? '' : ''}
      </Button>
    </div>
  );
}

function Faq({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.border}`, padding: '8px 0' }}>
      <div onClick={() => setOpen(!open)} style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>{open ? '▼' : ''}</span> {q}
      </div>
      {open && <p style={{ fontSize: 13, color: T.textMuted, margin: '8px 0 0 20px', lineHeight: 1.6 }}>{a}</p>}
    </div>
  );
}
